import styled, { CSSObject } from '@emotion/styled'

import { conditionalStyles } from 'component-library/__styling/conditional-styles'

import { colors, metrics } from 'component-library/__styling/settings'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { Product } from 'component-library/__product'
import { BreakpointKeys, Breakpoints } from 'component-library/__styling/settings/breakpoints'
import { ThemeMargins, ContainerWidth } from 'component-library/__styling/settings/metrics'

export interface RawProps {
    hasTopSpacing?: boolean
    hasBackgroundFill?: boolean
    verticalSpacing?: keyof ThemeMargins
    horizontalSpacing?: keyof ThemeMargins
    horizontalGutters?: keyof ThemeMargins
    verticalGutters?: [keyof ThemeMargins, keyof ThemeMargins]
    containerWidth?: ContainerWidth
    fillContainer?: boolean
    stretchColumns?: boolean
    positionOffset?: {
        direction: 'top' | 'bottom'
        value: number
    }
    stickyOffset?: number | undefined
}

export type BreakPointProps = { [key in BreakpointKeys]?: RawProps }

export interface Props extends RawProps {
    breakpoints?: BreakPointProps
}

const getStylesFromProps = (props: RawProps, kind: Product) => {
    const margins = metrics[kind].margins

    const horizontalSpacing = props.horizontalSpacing && calcRem(margins[props.horizontalSpacing])
    const verticalSpacing = props.verticalSpacing && calcRem(margins[props.verticalSpacing])
    const horizontalGutters = props.horizontalGutters && calcRem(margins[props.horizontalGutters])
    const topVerticalGutters = props.verticalGutters && calcRem(margins[props.verticalGutters[0]])
    const bottomVerticalGutters =
        props.verticalGutters && calcRem(margins[props.verticalGutters[1]])

    const positionOffsetDirection = props.positionOffset && props.positionOffset.direction
    const positionOffsetValue = props.positionOffset && props.positionOffset.value

    return {
        marginLeft: horizontalSpacing,
        marginRight: horizontalSpacing,
        marginBottom: verticalSpacing,
        paddingLeft: horizontalGutters,
        paddingRight: horizontalGutters,
        paddingTop: topVerticalGutters,
        paddingBottom: bottomVerticalGutters,

        flexGrow: props.fillContainer ? 1 : undefined,
        marginTop: props.hasTopSpacing ? verticalSpacing : undefined,

        ...conditionalStyles(props.stretchColumns, {
            display: 'flex',
            flexDirection: 'column',
        }),
        ...conditionalStyles(props.stickyOffset !== undefined, {
            position: 'sticky',
            top: `${props.stickyOffset}px`,
        }),
        ...conditionalStyles(props.containerWidth !== undefined, {
            maxWidth: props.containerWidth,
            marginLeft: 'auto',
            marginRight: 'auto',
        }),
        ...conditionalStyles(props.positionOffset !== undefined, {
            position: 'relative',
            marginTop:
                positionOffsetDirection === 'top'
                    ? positionOffsetValue && calcRem(-positionOffsetValue)
                    : undefined,
        }),
    }
}
type BreakpointKey = keyof Breakpoints
export const Box = styled('div')<Props>(
    /**
     *  The following takes an object in the shape of breakpoints and reduces that into media queries and styles.
     *  The purpose here is to apply different props at certain breakpoints.
     */
    ({
        theme,
        breakpoints,
        hasTopSpacing,
        hasBackgroundFill,
        verticalSpacing,
        horizontalSpacing,
        horizontalGutters,
        verticalGutters,
        containerWidth,
        fillContainer,
        stretchColumns,
        stickyOffset,
        positionOffset,
    }) => {
        const breakpointStyles = Object.keys(breakpoints || {}).reduce<CSSObject>((styles, key) => {
            const passedBreakpoints = breakpoints as BreakPointProps
            // cast the breakpoint vars - we know they'll equal something because keys will exist
            const props = passedBreakpoints[key as BreakpointKey]
            if (props) {
                styles[breakpoint(key as BreakpointKey)] = getStylesFromProps(props, theme.kind)
            }
            return styles
        }, {})

        return {
            ...getStylesFromProps(
                {
                    hasTopSpacing,
                    hasBackgroundFill,
                    verticalSpacing,
                    horizontalSpacing,
                    horizontalGutters,
                    verticalGutters,
                    containerWidth,
                    fillContainer,
                    stretchColumns,
                    stickyOffset,
                    positionOffset,
                },
                theme.kind
            ),
            ...breakpointStyles,
            backgroundColor: hasBackgroundFill ? colors.white : 'transparent',
        }
    }
)
Box.displayName = 'Box'
